import { Trans, useLingui } from '@lingui/react/macro';
import {
  BarChartOutlined,
  ProjectOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import { useGuildId } from '../hooks/useGuildId';
import { useProjectAuthority } from '../hooks/useProjectAuthority';
import { routes } from '../lib/routes';
import { useServerStore } from '../stores/useServerStore';
import Sidebar from './Sidebar';
import styles from './ServerSidebar.module.css';

/** Contextual navigation and channels for the currently selected Project. */
export default function ServerSidebar({
  onTaskClick,
  activeThreadId,
  activeChannelId,
}: {
  onTaskClick?: (threadId: string) => void;
  activeThreadId?: string;
  activeChannelId?: string | null;
}) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const guildId = useGuildId();
  const activeGuild = useServerStore((state) => state.guilds.find((guild) => guild.id === guildId));
  const authority = useProjectAuthority(guildId);
  const select = (target: string) => {
    if (runUnsavedGuards(undefined, i18n)) navigate(target);
  };
  const path = location.pathname;

  return (
    <aside className={`current-server-rail ${styles.rail}`} aria-label={t`Current Project`}>
      <header className="current-server-header">
        <span className="current-server-name">{activeGuild?.name ?? t`No Project selected`}</span>
      </header>
      <nav className="current-server-nav" aria-label={t`Project resources`}>
        <button
          type="button"
          className={path.endsWith('/insights') ? 'current-server-nav-active' : ''}
          disabled={!guildId}
          onClick={() => guildId && select(routes.insights(guildId))}
        >
          <BarChartOutlined aria-hidden="true" /> <Trans>Insights</Trans>
        </button>
        <button
          type="button"
          className={path.endsWith('/tasks') ? 'current-server-nav-active' : ''}
          disabled={!guildId}
          onClick={() => guildId && select(routes.tasks(guildId))}
        >
          <ProjectOutlined aria-hidden="true" /> <Trans>Tasks</Trans>
        </button>
        <button
          type="button"
          aria-label={t`Members`}
          className={path.includes('/members') ? 'current-server-nav-active' : ''}
          disabled={!guildId}
          onClick={() => guildId && select(routes.members(guildId))}
        >
          <TeamOutlined aria-hidden="true" /> <Trans>Members</Trans>
        </button>
        {authority.ready && authority.canManageProject && (
          <button
            type="button"
            aria-label={t`Settings`}
            className={path.match(/\/settings(?:\/|$)/) ? 'current-server-nav-active' : ''}
            onClick={() => guildId && select(routes.serverSettings(guildId))}
          >
            <SettingOutlined aria-hidden="true" /> <Trans>Settings</Trans>
          </button>
        )}
      </nav>
      <Sidebar
        serverOnly
        onTaskClick={onTaskClick}
        activeThreadId={activeThreadId}
        activeChannelId={activeChannelId}
      />
    </aside>
  );
}
